import React, { Suspense, lazy } from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import HealthInsuranceTicker from "@/components/HealthInsuranceTicker";
import Footer from "@/components/Footer";
import LazySection from "@/components/LazySection";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import { locationConfigs } from "@/data/locationData";
import { newsArticles } from "@/data/newsArticles";
import { DollarSign, ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const OfferBanner = lazy(() => import("@/components/OfferBanner"));
const InstagramSection = lazy(() => import("@/components/InstagramSection"));
const ServiceProcess = lazy(() => import("@/components/ServiceProcess"));
const FAQ = lazy(() => import("@/components/FAQ"));
const About = lazy(() => import("@/components/About"));
const TeamPreview = lazy(() => import("@/components/TeamPreview"));
const Treatments = lazy(() => import("@/components/Treatments"));
const GoogleMap = lazy(() => import("@/components/GoogleMap"));
const Contact = lazy(() => import("@/components/Contact"));
const Disclaimer = lazy(() => import("@/components/Disclaimer"));
const WhyChooseUs = lazy(() => import("@/components/WhyChooseUs"));
const Testimonials = lazy(() => import("@/components/Testimonials"));

const homepageFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Does Y3 Smiles Dental bulk bill?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Y3 Smiles Dental participates in the Child Dental Benefits Schedule (CDBS), which provides eligible children with up to $1,158 in dental benefits over a two-year period. Please ask our team to check your child\'s eligibility when you book. Adult bulk billing is not available, but we accept all major health funds and offer transparent pricing.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you accept my health fund?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, we accept all major Australian health funds including Bupa, Medibank, HCF, and many others. We offer instant HICAPS claims processing so you can receive your rebates on the spot.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you see patients who have not been to the dentist in a long time?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely. Many of our patients come to us after a gap in dental care. Our team takes a gentle, non-judgmental approach and will start with a thorough checkup to understand your current dental health before discussing any treatment options.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is parking available at the Broadmeadows clinic?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, parking is available nearby. The clinic is also accessible by public transport. Please call us if you need directions or have any accessibility requirements.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you accept the Child Dental Benefits Schedule (CDBS)?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! Y3 Smiles Dental accepts CDBS for eligible children aged 0–17 years who are Medicare eligible. We check eligibility and process claims directly.',
      },
    },
    {
      '@type': 'Question',
      name: 'What payment plans are available?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We offer interest-free payment plans up to 12 months for treatments over $300, and extended plans up to 24 months with low interest rates. We also accept cash, credit/debit cards, and EFTPOS.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you have same-day appointments?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, we offer same-day appointments especially for urgent and emergency dental cases. Call us at 03 9022 4442 and we will do our best to accommodate you on the same day.',
      },
    },
    {
      '@type': 'Question',
      name: 'How often should I get a check-up?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We recommend dental check-ups and cleans every 6 months for adults and children. Patients with higher cavity risk may need more frequent visits as determined by their dentist.',
      },
    },
    {
      '@type': 'Question',
      name: "What's covered under CDBS for children?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The Child Dental Benefits Schedule covers examinations, cleaning, X-rays, fillings, extractions, and other essential treatments. Eligible children receive up to $1,158 in benefits over 2 calendar years.',
      },
    },
    {
      '@type': 'Question',
      name: 'When should kids first visit the dentist?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Children should have their first dental visit by age 1 or within 6 months of their first tooth appearing. Early visits help establish good oral hygiene habits and monitor development.',
      },
    },
    {
      '@type': 'Question',
      name: 'What counts as a dental emergency?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Dental emergencies include severe tooth pain, knocked-out or broken teeth, facial swelling, bleeding that will not stop, dental abscesses, and any trauma to the teeth or mouth. Call 03 9022 4442 immediately.',
      },
    },
  ],
};

const IndexPage = () => {
  useScrollAnimation();
  useScrollToHash();

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Dentist Broadmeadows | Y3 Smiles Dental — Book Online"
        description="Y3 Smiles Dental in Broadmeadows offers family, children's, and emergency dental care. CDBS bulk billing available. Book your appointment today."
        canonicalUrl="https://y3smilesdental.com.au/"
        image="/og-shopfront.jpg"
      >
        <script type="application/ld+json">{JSON.stringify(homepageFaqSchema)}</script>
      </SEO>
      <Header />
      <main className="flex-grow">
        <Hero />
        
        {/* Pricing Page Promo */}
        <section className="py-12 bg-gradient-to-r from-primary/5 via-white to-secondary/5">
          <div className="container mx-auto px-6">
            <Card className="max-w-4xl mx-auto border-2 border-primary/20 shadow-lg hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 mb-2">
                      See Our Prices — No Surprises
                    </h2>
                    <p className="text-neutral-800 mb-2">
                      Check how much our dental treatments cost. We show you all prices clearly.
                    </p>
                    <p className="text-neutral-600 text-sm">
                      Prices listed are a guide and may vary depending on your individual clinical needs. Your dentist will provide a full treatment plan and cost estimate before any work begins.
                    </p>
                  </div>
                  <Button variant="booking" size="lg" asChild className="group flex-shrink-0">
                    <Link to="/patient-resources/pricing">
                      View Pricing
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <HealthInsuranceTicker />
        {/* NIB First Choice */}
        <section className="py-10 bg-white">
          <div className="container mx-auto px-6">
            <Card className="max-w-4xl mx-auto border border-primary/10 shadow-soft">
              <CardContent className="p-6 flex flex-col md:flex-row items-center gap-6">
                <img
                  src="/logos/nib.webp"
                  alt="NIB First Choice"
                  className="h-12 w-auto"
                  width={180}
                  height={48}
                  loading="lazy"
                  decoding="async"
                />
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-xl md:text-2xl font-bold text-neutral-800 mb-2">NIB First Choice Network Dentist</h2>
                  <p className="text-neutral-800">
                    Lower out-of-pocket costs for NIB members. Learn how it works.
                  </p>
                </div>
                <Button variant="outline" size="lg" asChild className="group">
                  <Link to="/nib-dentist">
                    Learn about NIB cover
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
          <LazySection minHeight={200}>
            <Suspense fallback={<div className="min-h-[160px]" />}>
              <About />
            </Suspense>
          </LazySection>
          <LazySection minHeight={400}>
            <Suspense fallback={<div className="min-h-[300px]" />}>
              <TeamPreview className="bg-neutral-50" />
            </Suspense>
          </LazySection>
          <LazySection minHeight={600}>
            <Suspense fallback={<div className="min-h-[400px]" />}>
              <Treatments />
            </Suspense>
          </LazySection>
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-4">News and Articles</h2>
              <p className="text-lg text-neutral-700">
                Practical dental guides and local updates from Y3 Smiles Dental.
              </p>
              <p className="text-neutral-600 mt-3">
                Looking to learn more about dental health? Our articles cover common questions about checkups, children&apos;s teeth, and what to expect from dental procedures.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {newsArticles.slice(0, 3).map((article) => (
                <Card key={article.slug} className="border border-primary/10 shadow-soft hover:shadow-medium transition-all">
                  <CardContent className="p-6 flex flex-col h-full">
                    <h3 className="text-xl font-semibold text-neutral-800 mb-3">{article.title}</h3>
                    <p className="text-neutral-700 mb-6 flex-grow">{article.excerpt}</p>
                    <Button variant="outline" asChild>
                      <Link to={`/news-and-articles/${article.slug}`} aria-label={`Read article: ${article.title}`}>Read article</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-10">
              <Button variant="booking" asChild>
                <Link to="/news-and-articles">View all News and Articles</Link>
              </Button>
            </div>
          </div>
        </section>
          <LazySection minHeight={260}>
            <Suspense fallback={<div className="min-h-[200px]" />}>
              <InstagramSection />
            </Suspense>
          </LazySection>
          <LazySection minHeight={180}>
            <Suspense fallback={<div className="min-h-[120px]" />}>
              <OfferBanner />
            </Suspense>
          </LazySection>
          <LazySection minHeight={260}>
            <Suspense fallback={<div className="min-h-[200px]" />}>
              <ServiceProcess />
            </Suspense>
          </LazySection>          {/* Why Broadmeadows Families Choose Y3 Smiles Dental */}
          <section className="py-16 bg-white">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6">
                  Why Broadmeadows Families Choose Y3 Smiles Dental
                </h2>
                <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                  Y3 Smiles Dental is located in Broadmeadows, conveniently accessible for families across Melbourne&apos;s northern suburbs. We offer on-site parking and are accessible by public transport. Our welcoming, modern clinic is designed with the whole family in mind — from young children having their first check-up to adults looking for gentle, dependable care.
                </p>
                <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                  We participate in the Child Dental Benefits Schedule (CDBS) for eligible patients, and aim to accommodate same-day appointments for dental emergencies. Our team values clear communication and culturally inclusive care, and we welcome patients from all backgrounds.
                </p>
                <p className="text-sm text-neutral-500 italic">
                  A proper assessment by your dentist is needed to determine the right treatment for you. We encourage patients to seek a second opinion if they have any concerns.
                </p>
              </div>
            </div>
          </section>          <LazySection minHeight={260}>
            <Suspense fallback={<div className="min-h-[200px]" />}>
              <FAQ />
            </Suspense>
          </LazySection>
        {/* <WhyChooseUs /> */}
        {/* <Testimonials /> */}
          <LazySection minHeight={400}>
            <Suspense fallback={<div className="min-h-[300px]" />}>
              <WhyChooseUs />
            </Suspense>
          </LazySection>
          <LazySection minHeight={400}>
            <Suspense fallback={<div className="min-h-[300px]" />}>
              <Testimonials />
            </Suspense>
          </LazySection>
          <LazySection minHeight={500}>
            <Suspense fallback={<div className="min-h-[400px]" />}>
              <GoogleMap
                locationName="Broadmeadows"
                locationData={locationConfigs.broadmeadows.locationData}
                openingHours={locationConfigs.broadmeadows.openingHours}
              />
            </Suspense>
          </LazySection>
          <LazySection minHeight={500}>
            <Suspense fallback={<div className="min-h-[400px]" />}>
              <Contact />
            </Suspense>
          </LazySection>
          <LazySection minHeight={100}>
            <Suspense fallback={<div className="min-h-[80px]" />}>
              <Disclaimer />
            </Suspense>
          </LazySection>

        {/* Service Areas Section */}
        <section className="py-16 bg-neutral-50">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-3">Serving Melbourne’s North</h2>
                <p className="text-lg text-neutral-700">
                  Based in Broadmeadows, we welcome patients from across Melbourne’s northern suburbs.
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-8">
                {[
                  { name: "Broadmeadows", slug: "broadmeadows", highlight: true },
                  { name: "Meadow Heights", slug: "meadow-heights" },
                  { name: "Greenvale", slug: "greenvale" },
                  { name: "Glenroy", slug: "glenroy" },
                  { name: "Craigieburn", slug: "craigieburn" },
                  { name: "Roxburgh Park", slug: "roxburgh-park" },
                  { name: "Campbellfield", slug: "campbellfield" },
                  { name: "Hadfield", slug: "hadfield" },
                ].map((area) => (
                  <Link
                    key={area.slug}
                    to={`/locations/${area.slug}`}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all hover:shadow-md ${
                      area.highlight
                        ? "bg-primary text-white border-primary font-semibold"
                        : "bg-white text-neutral-700 border-primary/20 hover:border-primary hover:text-primary"
                    }`}
                  >
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium">{area.name}</span>
                  </Link>
                ))}
              </div>
              <div className="text-center">
                <Button variant="outline" asChild>
                  <Link to="/service-areas">View All Service Areas <ArrowRight className="w-4 h-4 ml-2 inline" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default IndexPage;
